import { Link } from 'react-router-dom';
import usePageHead from '../hooks/usePageHead';
import { useScrollReveal, staggerProps } from '../hooks/useScrollReveal';
import { Image, StatRow } from '../components/ui';
import ConnectSection from '../components/connect/ConnectSection';
import NowStrip from '../components/home/NowStrip';
import Balance from '../components/home/Balance';
import Socials from '../components/home/Socials';
import { useLean } from '../context/LeanContext';
import profile from '../data/profile/profile.json';
import running from '../data/adventures/running.json';
import travel from '../data/adventures/travel.json';
import portrait from '../assets/Suraj.png';
import './homePage.css';

/* Derived, not hand-maintained: the old copy said "9 countries" while
   travel.json had grown to 12. */
const COUNTRY_COUNT = travel.countries.length;
const kmRun = running.heroStats.find((s) => s.label === 'km run');

const STATS = [
  profile.heroStats[0],
  kmRun,
  { value: String(COUNTRY_COUNT), label: 'countries' },
  running.heroStats.find((s) => s.label === 'races finished'),
];

/* The two doors. Headlines reuse the framing the old About section
   already had — "The developer" / "The person" — which was the seed of
   this whole split and the only place it was ever expressed. */
const DOORS = [
  {
    wing: 'work',
    to: '/work',
    kicker: 'the developer',
    title: 'Work',
    body: 'MSc Computer Science at Bonn, specialising in Machine Learning and distributed systems.',
    cta: 'Experience, projects, résumé',
  },
  {
    wing: 'life',
    to: '/life',
    kicker: 'the person',
    title: 'Life',
    body: 'Originally from Nepal, now based in Bonn. I find energy in movement.',
    cta: 'Adventures, writing, log',
  },
];

const HomePage = () => {
  const doors = useScrollReveal();
  // Shared with the navbar switch, so hovering "work | life" up there
  // tips this beam too.
  const { lean, setLean } = useLean();

  usePageHead({
    title: null,
    path: '/',
    description:
      'Suraj Giri, software engineer and MSc Computer Science student in Bonn, ' +
      'Germany. Two halves: the work, and everything else.',
  });

  return (
    <main id="main" className="home">
      <section className="home-hero">
        <div className="container home-hero__inner">
          <div className="home-hero__text">
            <p className="kicker">{profile.tagline}</p>
            <h1 className="home-hero__name">
              Hi, I&rsquo;m <em>{profile.name}</em>
            </h1>
            <p className="home-hero__bio">{profile.heroBio}</p>
            <Socials />
          </div>

          <div className="home-hero__portrait">
            <Image
              src={portrait}
              alt={`${profile.name}, smiling, outdoors`}
              ratio="3 / 4"
              priority
            />
          </div>
        </div>

        <div className="container">
          <StatRow stats={STATS} />
        </div>
      </section>

      {/* The seam. Everything above is shared; below, the site forks. */}
      <section className="home-doors" aria-labelledby="doors-heading">
        <div className="container">
          <h2 className="sr-only" id="doors-heading">Two ways in</h2>
          <Balance lean={lean} onLean={setLean} />
          <div className={`home-doors__grid ${doors.className}`} ref={doors.ref}>
            {DOORS.map((door, i) => (
              <Link
                key={door.wing}
                to={door.to}
                className={`door door--${door.wing} ${lean === door.wing ? 'is-lean' : ''}`}
                data-wing={door.wing}
                onMouseEnter={() => setLean(door.wing)}
                onMouseLeave={() => setLean('none')}
                onFocus={() => setLean(door.wing)}
                onBlur={() => setLean('none')}
                {...staggerProps(i)}
              >
                <span className="door__kicker">{door.kicker}</span>
                <span className="door__title">{door.title}</span>
                <span className="door__body">{door.body}</span>
                <span className="door__cta">
                  {door.cta} <span aria-hidden="true">&rarr;</span>
                </span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <NowStrip />
      <ConnectSection />
    </main>
  );
};

export default HomePage;
